import { readCsrfCookie } from '@/lib/api/cookies.js';
import { userApiFetch, userClient } from '@/lib/api/user-client.js';
import { queryClient } from '@/lib/query/client-instance.js';
import { queryKeys } from '@/lib/query/keys.js';
import { IDENTITY_STALE_TIME_MS } from '@/lib/query/policies.js';

import { userSessionStore, type UserDto } from './user-session-store.js';

/**
 * ОДНОРАЗОВОЕ приветственное сообщение после регистрации.
 *
 * ⚠️ Ловушка, ради которой всё это устроено именно так. Сервер отдаёт `welcome_message`
 * в ответе `GET /auth/me` РОВНО ОДИН РАЗ и тут же гасит флаг в БД
 * (`consumeWelcomeFlash`, `WHERE welcome_flash_at IS NULL`). Если положить весь ответ
 * в кэш Query, любой фоновый рефетч — переход между страницами, повторный монтаж
 * компонента, вторая вкладка — сожжёт сообщение на сервере и затрёт его в кэше
 * пустым значением. Пользователь не увидит приветствие ни разу, и воспроизвести это
 * потом невозможно: флаг уже погашен.
 *
 * Решение: `fetchMe` кладёт в кэш ТОЛЬКО `user`, а сообщение выхватывает до этого и
 * держит в отдельном сторе. В кэше Query его нет — значит и стирать нечего.
 */
let welcomeMessage: string | null = null;
const welcomeListeners = new Set<() => void>();

function emitWelcome(): void {
  for (const listener of welcomeListeners) listener();
}

export const welcomeMessageStore = {
  getSnapshot: (): string | null => welcomeMessage,
  subscribe(listener: () => void): () => void {
    welcomeListeners.add(listener);
    return () => {
      welcomeListeners.delete(listener);
    };
  },
  /** Вызывается экраном после показа: сообщение одноразовое и по смыслу, и по факту. */
  consume(): void {
    if (welcomeMessage === null) return;
    welcomeMessage = null;
    emitWelcome();
  },
};

/**
 * ЕДИНЫЙ queryFn для `/auth/me`. Им пользуются и бутстрап, и реактивные потребители,
 * поэтому обработка одноразового поля не может быть случайно обойдена.
 */
export async function fetchMe(): Promise<UserDto> {
  const response = await userApiFetch('get', '/auth/me');
  if (typeof response.welcome_message === 'string' && response.welcome_message !== '') {
    welcomeMessage = response.welcome_message;
    emitWelcome();
  }
  userSessionStore.updateUser(response.user);
  return response.user;
}

/**
 * Восстановление сессии при загрузке страницы.
 *
 * ⚠️ Начинаем с refresh, а НЕ с `/auth/me`. В памяти JS access-токена после перезагрузки
 * нет, и `/me` без него вернёт `token_invalid` — а на этот код автообновление намеренно
 * не срабатывает (обновление чинит протухший токен, а не отсутствующий). То есть
 * рассчитывать на перехватчик 401 здесь нельзя, он не сработает по построению.
 *
 * Наличие CSRF-cookie — дешёвый и точный признак «сессия вообще была»: она ставится
 * рядом с refresh-cookie и живёт столько же. Нет её — гость, и в сеть ходить незачем.
 */
export async function bootstrapUserAuth(): Promise<void> {
  if (readCsrfCookie() === null) {
    userSessionStore.clear();
    return;
  }

  const restored = await userClient.refreshOnce();
  if (!restored) return; // стор уже переведён в `guest` внутри refresh

  try {
    await queryClient.fetchQuery({
      queryKey: queryKeys.auth.me(),
      queryFn: fetchMe,
      staleTime: IDENTITY_STALE_TIME_MS,
    });
  } catch {
    // Токен обновился, но профиль не отдался: аккаунт заблокирован (401 account_blocked)
    // либо сеть отвалилась между двумя запросами. В обоих случаях авторизованным
    // пользователя считать нельзя.
    userSessionStore.clear();
  }
}
